/**
 * What kind of answer a CMMD Claude readiness reason names.
 *
 * CMMD-36441 answers the readiness read with a typed reason in an open string.
 * The server turn guard and the browser readiness poll both act on it, and
 * they have to agree: a reason one side reads as reconnect and the other does
 * not fails the turn at once on the server while the browser keeps polling
 * (FOR-3085). Both import the mapping from here.
 *
 * The mapping is a closed allowlist in both directions, the shape FOR-36356
 * used: a reason CMMD adds later maps to null, never to the wrong kind.
 */

export type ClaudeReadinessReasonCode =
  | "CREDENTIAL_EXPIRED_REFRESHING"
  | "CREDENTIAL_RECONNECT_REQUIRED"
  | "CREDENTIAL_GENERATION_NOT_APPLIED"
  | "RUNTIME_UNAVAILABLE";

/**
 * The reason literals Forge acts on, confirmed against CMMD-36441's readiness
 * service. If the route renames one, the rename lands here as a fall-through
 * to null, never as the wrong kind.
 *
 * Reading the reason is what makes `stale` legible. CMMD-36441 answers
 * `credential_reconnect_required` on a `stale` answer that still reports
 * `connected: true`, and the older `credential_generation_not_applied` on a
 * `stale` answer that simply has not landed yet. Those want opposite
 * handling, and both used to get "Reconnect Claude in Settings" because the
 * guard read only the delivery status.
 */
const CODE_BY_UPSTREAM_REASON: Record<string, ClaudeReadinessReasonCode> = {
  credential_expired_refreshing: "CREDENTIAL_EXPIRED_REFRESHING",
  credential_reconnect_required: "CREDENTIAL_RECONNECT_REQUIRED",
  credential_not_connected: "CREDENTIAL_RECONNECT_REQUIRED",
  credential_generation_not_applied: "CREDENTIAL_GENERATION_NOT_APPLIED",
  // CMMD-36473: the runtime row says the Environment cannot take a delivery
  // (boot failed, or a status that accepts no owner request). The credential
  // is not the problem and no wait applies it, so every surface names the
  // Environment instead. `lastErrorCode` carries the runtime's own error code.
  runtime_unavailable: "RUNTIME_UNAVAILABLE",
};

export function claudeReadinessReasonCode(
  reason: string | null | undefined,
): ClaudeReadinessReasonCode | null {
  if (typeof reason !== "string" || !Object.hasOwn(CODE_BY_UPSTREAM_REASON, reason)) return null;
  return CODE_BY_UPSTREAM_REASON[reason] ?? null;
}
